import React from 'react';
import styled from 'styled-components';
import { semanticColors } from '@/common/styles/colorTokens';
import { typography } from '@/common/styles/typographyTokens';
import { Button } from '@/common/components/button/Button';
import { FiAlertCircle, FiCheckCircle, FiXCircle, FiWifi } from 'react-icons/fi';

/**
 * 에러 유형
 */
type ErrorType = 'expired' | 'completed' | 'mismatch' | 'invalid' | 'network';

interface ErrorStepProps {
  errorType: ErrorType;
  onGoToLogin: () => void;
}

/**
 * 에러 메시지 및 아이콘 설정
 */
const errorConfig: Record<ErrorType, { 
  icon: React.ReactNode; 
  title: string; 
  message: string;
  iconColor: string;
}> = {
  expired: {
    icon: <FiAlertCircle size={48} />,
    title: '링크가 만료되었습니다',
    message: '비밀번호 변경 링크가 만료되었습니다.\n마스터에게 다시 요청해 주세요.',
    iconColor: '#f59e0b',
  },
  completed: {
    icon: <FiCheckCircle size={48} />,
    title: '이미 변경이 완료되었습니다',
    message: '이미 비밀번호가 변경되었습니다.\n새 비밀번호로 로그인해 주세요.',
    iconColor: '#10b981',
  },
  mismatch: {
    icon: <FiXCircle size={48} />,
    title: '본인인증 정보 불일치',
    message: '본인인증 정보가 일치하지 않습니다.\n등록된 정보와 동일한 인증을 진행해 주세요.',
    iconColor: '#ef4444',
  },
  invalid: {
    icon: <FiAlertCircle size={48} />,
    title: '유효하지 않은 링크',
    message: '유효하지 않은 비밀번호 변경 링크입니다.\n올바른 링크로 다시 접속해 주세요.',
    iconColor: '#ef4444',
  },
  network: {
    icon: <FiWifi size={48} />,
    title: '네트워크 오류',
    message: '네트워크 오류가 발생했습니다.\n잠시 후 다시 시도해 주세요.',
    iconColor: '#6366f1',
  },
};

/**
 * 에러 단계 컴포넌트
 * 토큰 만료, 이미 완료, 본인 불일치 등의 에러 표시
 */
const ErrorStep: React.FC<ErrorStepProps> = ({ errorType, onGoToLogin }) => {
  const config = errorConfig[errorType];

  return (
    <Container>
      <IconWrapper $color={config.iconColor}>
        {config.icon}
      </IconWrapper>
      
      <TextContainer>
        <Title>{config.title}</Title>
        <Message>{config.message}</Message>
      </TextContainer>

      <Button 
        $variant="gradient" 
        $size="md" 
        $isFull 
        onClick={onGoToLogin}
      >
        로그인 페이지로 이동
      </Button>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 32px;
  padding: 24px 0;
`;

const IconWrapper = styled.div<{ $color: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: ${({ $color }) => `${$color}15`};
  color: ${({ $color }) => $color};
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  text-align: center;
`;

const Title = styled.h2`
  ${typography.heading.h3}
  color: ${semanticColors.text.primary};
  margin: 0;
`;

const Message = styled.p`
  ${typography.paragraph.p1.default}
  color: ${semanticColors.text.secondary};
  margin: 0;
  white-space: pre-line;
  line-height: 1.6;
`;

export default ErrorStep;
